import {
  EllipsedText,
  EmptyState,
  FlexColumn,
  ImageThumbnail,
  MaterialIcon,
  useMoment,
} from '@gorila-shared-ui/components';
import { Badge } from 'baseui/badge';
import { LoadingSpinner } from 'baseui/button/styled-components';
import { StyledDivider } from 'baseui/divider';
import { LabelSmall, LabelXSmall } from 'baseui/typography';
import { useMemo } from 'react';
import { useFilesDisplay } from '../../../../hooks/useFilesDisplay';
import { useStyles } from '../../../../hooks/useStyles';
import { FileDisplay } from '../../../../types/app';
import { FleetDriverPhotoDetail } from '../../../../types/fleet';
import { truncateNumber } from '../../../../utils/numbers';
import DataDescription from '../../../shared/DataDescription';
import OperationalStatusLabel from '../../../shared/OperationalStatusLabel';
import { StyledTooltip } from '../../../ui/StyledTooltip';

type Props = Readonly<{
  detail: FleetDriverPhotoDetail;
  onImageClick: (display: FileDisplay) => void;
}>;

function FleetDriverPhotoDetailCard({ detail, onImageClick }: Props) {
  const { css, theme } = useStyles();
  const { operationalState } = detail;
  const { formatDateFromTs } = useMoment();
  const detailPhotos = useMemo(() => {
    return detail.matchPhoto ? [detail.photo, detail.matchPhoto] : [detail.photo];
  }, [detail.photo, detail.matchPhoto]);
  const { files: photos, loading } = useFilesDisplay(detailPhotos);
  const file = photos?.[0];
  const matchFile = photos?.[1];
  const isManualPhoto = !detail.assetId;

  return (
    <FlexColumn
      classNames={css({
        ...theme.borders.border200,
        borderRadius: theme.borders.radius400,
        overflow: 'hidden',
        height: '100%',
      })}
      gap={0}
    >
      <Badge
        content={isManualPhoto ? 'Manual' : 'Auto'}
        shape="pill"
        overrides={{
          Root: {
            style: {
              height: '150px',
              width: '100%',
              background: theme.colors.gray100,
              overflow: 'hidden',
              position: 'relative',
            },
          },
        }}
        horizontalOffset={theme.sizing.scale200}
        verticalOffset={theme.sizing.scale200}
        color="primary"
      >
        {!file?.url && (
          <EmptyState style={{ minHeight: '150px' }}>
            {loading && <LoadingSpinner />}
            {!loading && (
              <MaterialIcon
                name="image"
                size="30px"
              />
            )}
          </EmptyState>
        )}
        {file?.url && (
          <>
            <ImageThumbnail
              url={file?.url}
              onClick={() => {
                onImageClick(file);
              }}
              imageStyle={{
                height: '150px',
                width: '100%',
                objectFit: 'contain',
                cursor: 'pointer',
              }}
            />
            {matchFile?.url && (
              <StyledTooltip
                content={
                  detail.confidence && detail.similarity ? (
                    <FlexColumn gap={theme.sizing.scale200}>
                      <LabelXSmall color={theme.colors.contentInversePrimary}>Coincidencia</LabelXSmall>
                      <LabelXSmall color={theme.colors.contentInversePrimary}>
                        Similitud: {truncateNumber(detail.similarity, 2)}%
                      </LabelXSmall>
                      <LabelXSmall color={theme.colors.contentInversePrimary}>
                        Confianza: {truncateNumber(detail.confidence, 2)}%
                      </LabelXSmall>
                    </FlexColumn>
                  ) : undefined
                }
                placement="top"
                ignoreBoundary
                popoverMargin={55}
                showArrow={false}
              >
                <ImageThumbnail
                  url={matchFile.url}
                  onClick={
                    file
                      ? () => {
                          onImageClick(file);
                        }
                      : undefined
                  }
                  imageStyle={{
                    height: '60px',
                    width: '40px',
                    objectFit: 'contain',
                    cursor: 'pointer',
                    position: 'absolute',
                    right: theme.sizing.scale200,
                    bottom: theme.sizing.scale200,
                    borderRadius: theme.borders.inputBorderRadius,
                    overflow: 'hidden',
                    display: 'block',
                  }}
                />
              </StyledTooltip>
            )}
          </>
        )}
      </Badge>
      <FlexColumn
        classNames={css({ padding: theme.sizing.scale600 })}
        gap={theme.sizing.scale300}
      >
        <DataDescription
          label="Fecha de captura:"
          content={formatDateFromTs(detail.created)}
        />
        {operationalState && (
          <>
            <StyledDivider style={{ width: '100%', margin: 0 }} />
            <LabelSmall style={{ fontWeight: '700' }}>
              <EllipsedText
                inline={false}
                breakAll
                maxLines={7}
              >
                {detail.label}
              </EllipsedText>
            </LabelSmall>
            <StyledTooltip
              content="Estado operativo"
              ignoreBoundary
              showArrow={false}
              placement="top"
            >
              <OperationalStatusLabel operationalState={operationalState} />
            </StyledTooltip>
            <DataDescription
              label="Fecha:"
              content={formatDateFromTs(detail.positionTs)}
            />
          </>
        )}
      </FlexColumn>
    </FlexColumn>
  );
}

export default FleetDriverPhotoDetailCard;
